/**
 * Worker see {@link https://github.com/nolanlawson/promise-worker promise-worker}
 */

let AppWorker; // app worker func
let WORKER; // worker instance

if (process.env.NODE_ENV === 'test' || process.env.VUE_APP_IS_STYLEGUIDE) {
    // ONLY TEST ENV
    // At that time mocha has problems with worker testing,
    // so, on test build - it's disabled.
    // I will fix as soon as I find out decision.
    AppWorker = function appWorker(stringUrl) {
        this.url = stringUrl;
        this.onmessage = () => {
        };
        this.postMessage = () => {
            this.onmessage();
        };
    };
    WORKER = new AppWorker();
} else {
    /**
     * NON-TEST ENV
     * Real worker and polyfill.
     */
    /* eslint-disable import/no-webpack-loader-syntax */
    /* eslint-disable global-require */
    require('pseudo-worker/polyfill');
    const PromiseWorker = require('promise-worker');
    AppWorker = require('worker-loader!@/web-worker');
    /* eslint-enable import/no-webpack-loader-syntax */
    /* eslint-enable global-require */
    WORKER = new PromiseWorker(new AppWorker());
}


/**
 * Prepare file.
 *
 * @param {file} file
 * @param {string} method - FileReader method, reader[method)(file)
 *
 * @returns {Promise<any>}
 */
const makeFileReaderPromise = (file, method) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = resolve;
    reader.onerror = reject;
    reader[method](file);
});

// CSV worker api
class WorkerApi {
    constructor() {
        this.worker = WORKER;
    }

    /**
     * Post message method.
     *
     * @param {string} type - worker api type. {apiName}.{methodName}
     * @param {array|object} data - worker api method arguments (array)
     * or worker registered method data (object)
     */
    postMessage(type, data) {
        return this.worker.postMessage({type, data});
    }
}

class CustomWorkerMethod extends WorkerApi {
    /**
     * @param {string} id - method id
     * @param unegisterApiHandler - private handler for remove method in api methods list
     */
    constructor(id, unegisterApiHandler) {
        super();
        this.id = id;

        this.unregister = async () => {
            const result = await this.postMessage('unregister', {id: this.id});
            unegisterApiHandler(this.id);
            return result;
        };
    }

    exec(...args) {
        return this.postMessage('exec', {
            id: this.id,
            args,
        });
    }
}

class CustomMethodsApi extends WorkerApi {
    constructor() {
        super();
        this.methods = [];
    }

    /**
     * Register function in worker {@link http://html5-demos.appspot.com/static/workers/transferables/index.html workers-transferables}.
     *
     * @param {function} callback - registered simple function.
     */
    async register(callback) {
        const name = callback.name || callback.constructor.name || `callback`;
        const methodString = callback.toString();

        const data = {
            name,
            args: methodString.substring(methodString.indexOf('(') + 1, methodString.indexOf(')')),
            body: methodString.substring(methodString.indexOf('{') + 1, methodString.lastIndexOf('}')),
        };

        const unregisterApiMethodHandler = (id) => {
            this.methods = this.methods.filter(method => method.id !== id);
        };

        const id = await this.postMessage('register', data);
        const method = new CustomWorkerMethod(id, unregisterApiMethodHandler);

        this.methods.push(method);

        return method;
    }

    unregisterAll() {
        return Promise.all([...this.methods].map(method => method.unregister()));
    }
}

class CSVApi extends WorkerApi {
    async parse(file, settings = {}) {
        const [mimeTypeReader, fileSourceReader] = await Promise.all([
            makeFileReaderPromise(file.slice(0, 4), 'readAsArrayBuffer'),
            makeFileReaderPromise(file, 'readAsText'),
        ]);
        const signatureSource = [...new Uint8Array(mimeTypeReader.target.result)];
        const fileSource = fileSourceReader.target.result;
        const fileObject = {
            name: file.name,
            type: file.type,
            settings,
            signatureSource,
            fileSource,
        };

        return this.postMessage('csv.parse', [fileObject]);
    }
}

/**
 * Worker client-side Api {@link https://github.com/nolanlawson/promise-worker promise-worker}.
 *
 * @type {PromiseWorker}
 */
export default ({
    csv: new CSVApi(),
    method: new CustomMethodsApi(),
    /* eslint-disable no-underscore-dangle */
    terminate: () => WORKER._worker.terminate(),
    /* eslint-enable no-underscore-dangle */
});
